/**
 * Time Entry Service (Simplified)
 *
 * Handles check-in/check-out for technician work days.
 * One row per work session: check_in_time + check_out_time.
 * GPS captured at both events. Late entries supported.
 *
 * DB table: time_entries (migration 039)
 *
 * @module services/timeEntryService
 */

import { supabase } from './supabase';

const SELECT_WITH_RELATIONS = `
  *,
  project:projects(id, project_number, title),
  task:tasks(id, title, task_number)
`;

/**
 * Get current GPS location
 * @returns {Promise<{latitude, longitude, accuracy} | null>}
 */
const getGPSLocation = () => {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
};

/**
 * Check-in: start a work session
 * @param {Object} data
 * @param {string} data.project_id - Required
 * @param {string} [data.task_id]
 * @param {string} [data.notes]
 * @returns {Promise<{data, error}>}
 */
export const checkIn = async ({ project_id, task_id, notes }) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Check for active (unclosed) entry
    const { data: active } = await supabase
      .from('time_entries')
      .select('id')
      .eq('user_id', user.id)
      .is('check_out_time', null)
      .maybeSingle();

    if (active) {
      throw new Error('Ya tiene una jornada activa. Debe finalizar antes de iniciar otra.');
    }

    const location = await getGPSLocation();

    const { data, error } = await supabase
      .from('time_entries')
      .insert({
        user_id: user.id,
        project_id,
        task_id: task_id || null,
        check_in_time: new Date().toISOString(),
        check_in_latitude: location?.latitude || null,
        check_in_longitude: location?.longitude || null,
        check_in_accuracy: location?.accuracy || null,
        notes: notes || null,
      })
      .select(SELECT_WITH_RELATIONS)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Check-in error:', error);
    return { data: null, error };
  }
};

/**
 * Check-out: end the current work session
 * @param {Object} data
 * @param {string} data.entry_id - The active entry ID
 * @param {string} [data.notes]
 * @returns {Promise<{data, error}>}
 */
export const checkOut = async ({ entry_id, notes }) => {
  try {
    const location = await getGPSLocation();
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('time_entries')
      .update({
        check_out_time: now,
        check_out_latitude: location?.latitude || null,
        check_out_longitude: location?.longitude || null,
        check_out_accuracy: location?.accuracy || null,
        notes: notes || null,
      })
      .eq('id', entry_id)
      .select(SELECT_WITH_RELATIONS)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Check-out error:', error);
    return { data: null, error };
  }
};

/**
 * Late check-in: register a past start time
 * @param {Object} data
 * @param {string} data.project_id
 * @param {string} [data.task_id]
 * @param {string} data.check_in_time - ISO string of actual start time
 * @param {string} data.reason - Required reason for late entry
 * @returns {Promise<{data, error}>}
 */
export const lateCheckIn = async ({ project_id, task_id, check_in_time, reason }) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate: no more than 4 hours ago
    const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000);
    const requestedTime = new Date(check_in_time);
    if (requestedTime < fourHoursAgo) {
      throw new Error('No se puede registrar una hora de inicio de hace más de 4 horas.');
    }
    if (requestedTime > new Date()) {
      throw new Error('No se puede registrar una hora futura.');
    }

    // Check for active entry
    const { data: active } = await supabase
      .from('time_entries')
      .select('id')
      .eq('user_id', user.id)
      .is('check_out_time', null)
      .maybeSingle();

    if (active) {
      throw new Error('Ya tiene una jornada activa. Debe finalizar antes de iniciar otra.');
    }

    // GPS captured NOW (cannot be faked)
    const location = await getGPSLocation();

    const { data, error } = await supabase
      .from('time_entries')
      .insert({
        user_id: user.id,
        project_id,
        task_id: task_id || null,
        check_in_time: requestedTime.toISOString(),
        check_in_latitude: location?.latitude || null,
        check_in_longitude: location?.longitude || null,
        check_in_accuracy: location?.accuracy || null,
        is_late_entry: true,
        late_entry_reason: reason,
      })
      .select(SELECT_WITH_RELATIONS)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Late check-in error:', error);
    return { data: null, error };
  }
};

/**
 * Ventana máxima hacia atrás para DECLARAR una jornada ya terminada, en días.
 *
 * Gemelo literal de `MAX_BACKDATE_DAYS` en
 * `mcp-server/src/tools/timeEntryTools.js`. Si cambia uno, cambia el otro.
 * Es DISTINTA de la ventana de `lateCheckIn` (4 horas) a propósito: aquélla
 * abre una jornada en curso, ésta declara una que ya terminó.
 */
export const MAX_BACKDATE_DAYS = 7;

/** Techo de horas de UNA sesión declarada. `total_hours` es numeric(5,2): la
 *  base acepta 999.99, y un error de tipeo acá se factura. */
export const MAX_SESSION_HOURS = 24;

/**
 * Declarar una jornada YA TERMINADA (#598).
 *
 * `checkIn`/`checkOut` sellan el reloj en el momento y `lateCheckIn` sólo ABRE
 * una jornada (tope 4 h). Nada de eso permite decir "ayer trabajé 4 horas en el
 * CIMA": esta función es lo que faltaba, en los dos carriles a la vez.
 *
 * Sin GPS: no se midió nada en el lugar, y una coordenada inventada es peor que
 * ninguna. La fila se marca `is_late_entry` con su motivo, igual que un late
 * check-in.
 *
 * `total_hours` se escribe A MANO: el trigger `calculate_time_entry_hours` es
 * BEFORE UPDATE, así que en un INSERT ya cerrado no corre y la fila quedaría en
 * NULL — registrada y a la vez perdida para todo reporte.
 *
 * @param {Object} data
 * @param {string} data.project_id - Obligatorio: sin proyecto no se costea
 * @param {string} data.start_time - ISO string de la hora real de inicio
 * @param {number} data.hours - Horas trabajadas en esa sesión
 * @param {string} data.reason - Obligatorio: por qué se registra después
 * @param {string} [data.task_id]
 * @param {string} [data.notes]
 * @returns {Promise<{data, error}>}
 */
export const logWorkedHours = async ({ project_id, start_time, hours, reason, task_id, notes }) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!project_id) throw new Error('Hay que decir a qué proyecto se imputan las horas.');
    if (!reason || String(reason).trim().length === 0) {
      throw new Error('Hay que decir por qué se registra la jornada después.');
    }

    const numericHours = Number(hours);
    if (!Number.isFinite(numericHours) || numericHours <= 0) {
      throw new Error('Las horas tienen que ser un número mayor que 0.');
    }
    if (numericHours > MAX_SESSION_HOURS) {
      throw new Error(`Una jornada no puede tener más de ${MAX_SESSION_HOURS} horas.`);
    }

    const start = new Date(start_time);
    if (Number.isNaN(start.getTime())) throw new Error('La hora de inicio no es válida.');

    const now = Date.now();
    if (start.getTime() < now - MAX_BACKDATE_DAYS * 24 * 60 * 60 * 1000) {
      throw new Error(`No se puede declarar una jornada que empezó hace más de ${MAX_BACKDATE_DAYS} días.`);
    }

    const end = new Date(start.getTime() + numericHours * 60 * 60 * 1000);
    if (start.getTime() > now) throw new Error('No se puede declarar una jornada que todavía no empezó.');
    if (end.getTime() > now) throw new Error('La jornada terminaría en el futuro.');

    // Solape: la misma hora en dos filas es la misma hora facturada dos veces.
    const ventanaDesde = new Date(start.getTime() - MAX_SESSION_HOURS * 60 * 60 * 1000).toISOString();
    const { data: vecinas } = await supabase
      .from('time_entries')
      .select('id, check_in_time, check_out_time')
      .eq('user_id', user.id)
      .gte('check_in_time', ventanaDesde)
      .lte('check_in_time', end.toISOString());

    const solapada = (vecinas || []).find((e) => {
      const eStart = new Date(e.check_in_time).getTime();
      const eEnd = e.check_out_time ? new Date(e.check_out_time).getTime() : now;
      return eStart < end.getTime() && eEnd > start.getTime();
    });
    if (solapada) throw new Error('Esas horas se pisan con una jornada ya registrada.');

    const { data, error } = await supabase
      .from('time_entries')
      .insert({
        user_id: user.id,
        project_id,
        task_id: task_id || null,
        check_in_time: start.toISOString(),
        check_out_time: end.toISOString(),
        total_hours: Math.round(numericHours * 100) / 100,
        is_late_entry: true,
        late_entry_reason: String(reason).trim(),
        notes: notes || null,
      })
      .select(SELECT_WITH_RELATIONS)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Log worked hours error:', error);
    return { data: null, error };
  }
};

/**
 * Get current active (unclosed) entry for the logged-in user
 * @returns {Promise<{data, error}>}
 */
export const getActiveEntry = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('time_entries')
      .select(SELECT_WITH_RELATIONS)
      .eq('user_id', user.id)
      .is('check_out_time', null)
      .order('check_in_time', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Get active entry error:', error);
    return { data: null, error };
  }
};

/**
 * Get today's entries for the logged-in user
 * @returns {Promise<{data, totalHours, error}>}
 */
export const getTodayEntries = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const { data, error } = await supabase
      .from('time_entries')
      .select(SELECT_WITH_RELATIONS)
      .eq('user_id', user.id)
      .gte('check_in_time', today.toISOString())
      .lt('check_in_time', tomorrow.toISOString())
      .order('check_in_time', { ascending: false });

    if (error) throw error;

    const totalHours = (data || [])
      .filter(e => e.total_hours)
      .reduce((sum, e) => sum + Number(e.total_hours), 0);

    return { data: data || [], totalHours: Math.round(totalHours * 100) / 100, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Get today entries error:', error);
    return { data: [], totalHours: 0, error };
  }
};

/**
 * Get entries for a date range
 * @param {Object} filters
 * @param {string} [filters.from] - ISO date
 * @param {string} [filters.to] - ISO date
 * @param {number} [filters.limit] - Max results
 * @returns {Promise<{data, totalHours, error}>}
 */
export const getEntries = async (filters = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    let query = supabase
      .from('time_entries')
      .select(SELECT_WITH_RELATIONS)
      .eq('user_id', user.id);

    if (filters.from) query = query.gte('check_in_time', filters.from);
    if (filters.to) query = query.lte('check_in_time', filters.to);

    query = query.order('check_in_time', { ascending: false });

    if (filters.limit) query = query.limit(filters.limit);

    const { data, error } = await query;
    if (error) throw error;

    const totalHours = (data || [])
      .filter(e => e.total_hours)
      .reduce((sum, e) => sum + Number(e.total_hours), 0);

    return { data: data || [], totalHours: Math.round(totalHours * 100) / 100, error: null };
  } catch (error) {
    console.error('[TIME_ENTRY] Get entries error:', error);
    return { data: [], totalHours: 0, error };
  }
};

const timeEntryService = {
  checkIn,
  checkOut,
  lateCheckIn,
  logWorkedHours,
  getActiveEntry,
  getTodayEntries,
  getEntries,
};

export default timeEntryService;
